/**
 * WELCHER WEG GILT, WENN ZWEI VORGÄNGE ZUSAMMENGEFÜHRT WERDEN
 * (PO 22.09.2026 — die Gegenüberstellung).
 *
 * Der PO hat vier Fälle skizziert und dabei nach der Form des VERLIERERS
 * gegliedert. Die Prüfung hat gezeigt: Es zählt auch die Form des SIEGERS.
 * Diese Datei ist die Antwort darauf — eine Tabelle statt vier Sonderfälle.
 *
 * ══ DIE ZWEI FORMEN ══════════════════════════════════════════════════════
 * Ein Vorgang trägt seinen Schriftwechsel ENTWEDER selbst (keine To-dos) ODER
 * an seinen To-dos. Das ist die Invariante; alles hier folgt daraus.
 *
 * ══ DIE TABELLE ══════════════════════════════════════════════════════════
 * | Sieger   | Verlierer | Was anhakbar ist                                |
 * |----------|-----------|-------------------------------------------------|
 * | schlank  | schlank   | seine Anhänge einzeln — ODER der ganze Vorgang   |
 * |          |           | als To-do (PO-Fälle 1 und 2)                     |
 * | schlank  | gegliedert| seine To-dos                                     |
 * | gegliedert | schlank | NUR der ganze Vorgang als To-do (PO-Fall 3)      |
 * | gegliedert | gegliedert | seine To-dos (PO-Fall 4)                     |
 *
 * ══ WARUM „ALS TO-DO" ERZWUNGEN IST, WENN DER SIEGER TO-DOS HAT ══════════
 * Das ist die Zeile, die in der Skizze des PO nur als „Vorschlag" stand.
 * Sie ist keiner.
 *
 * Ein flacher Merge zöge den Schriftwechsel des Verlierers frei an den
 * Sieger. Der Server stellt danach die Invariante wieder her — und zieht
 * dabei ALLES frei Hängende in ein To-do, das den Betreff des SIEGERS trägt.
 * Der Betreff des Verlierers wäre damit spurlos verschwunden, und seine
 * Arbeit stünde unter fremdem Namen. Kein Fehler, keine Meldung, und von
 * außen nicht als Verlust erkennbar.
 *
 * Als To-do übernommen behält der Verlierer dagegen seinen Betreff — er wird
 * zur Überschrift des To-dos. Genau das, was der PO für diesen Fall
 * beschrieben hat.
 */

/** Die Form eines Vorgangs — mehr braucht die Entscheidung nicht. */
export interface VorgangsForm {
    /** Hat er eigene To-dos? */
    hatTodos: boolean;
}

export interface Wege {
    /** Seine To-dos sind einzeln anhakbar. */
    todosWaehlbar: boolean;
    /** „Den ganzen Vorgang als To-do übernehmen" steht zur Wahl. */
    alsTodoMoeglich: boolean;
    /** … und ist der EINZIGE Weg — nicht bloß einer von zweien. */
    alsTodoErzwungen: boolean;
    /** Seine Notizen und Dokumente sind einzeln anhakbar. */
    einzelnWaehlbar: boolean;
}

export function wegeFuer(sieger: VorgangsForm, verlierer: VorgangsForm): Wege {
    if (verlierer.hatTodos) {
        // Seine Arbeit ist bereits gegliedert — die To-dos sind die Einheit.
        // „Als To-do" gäbe es hier ohnehin nicht: Der Server lehnt einen
        // Vorgang mit To-dos dafür ab.
        return {
            todosWaehlbar: true,
            alsTodoMoeglich: false,
            alsTodoErzwungen: false,
            einzelnWaehlbar: false,
        };
    }
    if (sieger.hatTodos) {
        // Siehe oben — hier ist „als To-do" der einzige Weg, der den Betreff
        // des Verlierers erhält.
        return {
            todosWaehlbar: false,
            alsTodoMoeglich: true,
            alsTodoErzwungen: true,
            einzelnWaehlbar: false,
        };
    }
    // Beide schlank: Der Anwender entscheidet, ob es dasselbe zweimal war
    // (dann einzeln) oder zwei Arbeiten, die zusammengehören (dann als To-do).
    return {
        todosWaehlbar: false,
        alsTodoMoeglich: true,
        alsTodoErzwungen: false,
        einzelnWaehlbar: true,
    };
}

/**
 * Was mit dem Quellvorgang geschieht — in der Sprache des Anwenders, VOR der
 * Handlung.
 *
 * Die Zahl ist der Punkt: „geht in den Papierkorb" und „bleibt mit zwei
 * To-dos stehen" sind verschiedene Ausgänge, und wer sie erst hinterher
 * sieht, hat die Entscheidung nicht getroffen.
 */
export function ausgangText(args: {
    /** To-dos, die der Verlierer HAT (0, wenn er schlank ist). */
    todosGesamt: number;
    /** To-dos, die angehakt sind. */
    todosGewaehlt: number;
    /** Wird der ganze Vorgang als To-do übernommen? */
    alsTodo: boolean;
    titel: string;
}): string {
    const t = args.titel.trim() || "(ohne Betreff)";
    if (args.alsTodo) {
        return `„${t}" wird ein To-do im Zielvorgang und behält seinen Betreff als Überschrift. Der Vorgang selbst geht danach in den Papierkorb.`;
    }
    if (args.todosGesamt === 0) {
        return `Was nicht angehakt ist, bleibt bei „${t}" und geht mit ihm in den Papierkorb — gelöscht wird nichts.`;
    }
    const rest = args.todosGesamt - args.todosGewaehlt;
    if (rest <= 0) {
        return `Alle To-dos wandern. „${t}" geht danach in den Papierkorb.`;
    }
    return `${args.todosGewaehlt} von ${args.todosGesamt} To-dos wandern. „${t}" bleibt mit ${
        rest === 1 ? "dem übrigen" : `den übrigen ${rest}`
    } stehen.`;
}
